import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/authStore';
import { usePermission } from '@/hooks/usePermission';
import {
  contextSchema,
  detailSchema,
  listSchema,
  transactionLookupSchema,
  type MonitoringFilters,
  type DecisionBody,
} from './types';

export const monitoringKey = (tenant: string | undefined) =>
  ['transaction-monitoring', tenant] as const;
export function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message: unknown = error.message;
    const text =
      typeof message === 'string'
        ? message
        : Array.isArray(message) && message.every((v) => typeof v === 'string')
          ? message.join(', ')
          : 'Request failed';
    const code = 'statusCode' in error ? String(error.statusCode) : '';
    return code ? `${code}: ${text}` : text;
  }
  return 'Unable to complete the request. Please try again.';
}
export function errorStatus(error: unknown): number | null {
  return error && typeof error === 'object' && 'statusCode' in error
    ? Number(error.statusCode)
    : null;
}
export function useScreeningList(filters: MonitoringFilters) {
  const tenant = useAuthStore((s) => s.user?.tenant_cod);
  const { has, hasRole } = usePermission();
  return useQuery({
    queryKey: [...monitoringKey(tenant), 'list', filters],
    queryFn: async ({ signal }) =>
      listSchema.parse(
        await api.get<unknown>('/api/compliance/transactions', { params: filters, signal }),
      ),
    enabled: !!tenant && hasRole('cbs_admin') && has('COMPLIANCE_VIEW'),
    retry: false,
    gcTime: 0,
  });
}
export function useScreening(id: string | null) {
  const tenant = useAuthStore((s) => s.user?.tenant_cod);
  const { has, hasRole } = usePermission();
  return useQuery({
    queryKey: [...monitoringKey(tenant), 'detail', id],
    queryFn: async ({ signal }) =>
      detailSchema.parse(await api.get<unknown>(`/api/compliance/transactions/${id}`, { signal })),
    enabled:
      !!tenant &&
      !!id &&
      /^[1-9]\d{0,18}$/.test(id) &&
      hasRole('cbs_admin') &&
      has('COMPLIANCE_VIEW'),
    retry: false,
    gcTime: 0,
  });
}
const SCREENING_ID = /^[1-9]\d{0,18}$/;
const TRANSACTION_ID = /^[A-Za-z0-9][A-Za-z0-9-]{0,99}$/;
/** Exact server-side lookup by CBS transaction_id; never a list scan or an inferred ID. */
export function useScreeningForTransaction(
  type: 'outbound' | 'incoming',
  transactionId: string | null | undefined,
) {
  const tenant = useAuthStore((s) => s.user?.tenant_cod);
  const { has, hasRole } = usePermission();
  return useQuery({
    queryKey: [...monitoringKey(tenant), 'by-transaction', type, transactionId],
    queryFn: async ({ signal }) =>
      transactionLookupSchema.parse(
        await api.get<unknown>(
          `/api/compliance/transactions/by-transaction/${type}/${encodeURIComponent(transactionId!)}`,
          { signal },
        ),
      ),
    enabled:
      !!tenant &&
      !!transactionId &&
      TRANSACTION_ID.test(transactionId) &&
      hasRole('cbs_admin') &&
      has('COMPLIANCE_VIEW'),
    retry: false,
    gcTime: 0,
  });
}
export function useScreeningContext(id: string) {
  const tenant = useAuthStore((s) => s.user?.tenant_cod);
  const { has, hasRole } = usePermission();
  return useQuery({
    queryKey: [...monitoringKey(tenant), 'context', id],
    queryFn: async ({ signal }) =>
      contextSchema.parse(
        await api.get<unknown>(`/api/compliance/transactions/${id}/context`, { signal }),
      ),
    enabled: !!tenant && SCREENING_ID.test(id) && hasRole('cbs_admin') && has('COMPLIANCE_VIEW'),
    retry: false,
    gcTime: 0,
  });
}
export function useScreeningAction(id: string) {
  const tenant = useAuthStore((s) => s.user?.tenant_cod);
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      action,
      body,
    }: {
      action: 'sync' | 'decision' | 'resubmit';
      body?: DecisionBody;
    }) => {
      const result = detailSchema.parse(
        await api.post<unknown>(`/api/compliance/transactions/${id}/${action}`, body),
      );
      // A tenant switch must never write a late response into the new tenant cache.
      if (useAuthStore.getState().user?.tenant_cod === tenant) {
        client.setQueryData([...monitoringKey(tenant), 'detail', id], result);
        await client.invalidateQueries({ queryKey: monitoringKey(tenant) });
      }
      return result;
    },
    retry: false,
  });
}
